import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import {
  listBackOfficeDocs,
  removeBackOfficeDocs,
  countBackOfficeDocs,
} from "@/lib/offlineQueue";
import { toast } from "sonner";

/**
 * useBackOfficeSync — background flush of the back-office IndexedDB queue.
 *
 * Batches GRN / Stock Entry / Wastage / Transfer docs and replays them via
 * POST /api/back-office-sync/flush. Same on-reconnect + 30s poll behaviour
 * as the POS offline sync.
 */
export default function useBackOfficeSync() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [pending, setPending] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const flushingRef = useRef(false);

  const refreshCount = useCallback(async () => {
    try {
      const n = await countBackOfficeDocs();
      setPending(n);
    } catch (_e) {
      /* ignore */
    }
  }, []);

  const flushNow = useCallback(async () => {
    if (flushingRef.current) return { skipped: true };
    flushingRef.current = true;
    setFlushing(true);
    try {
      const items = await listBackOfficeDocs();
      if (items.length === 0) return { processed: 0 };
      const docs = items.map((it) => ({
        client_uuid: it.client_uuid,
        offline_at: it.queued_at,
        doc_type: it.doc_type,
        label: it.label || "",
        payload: it.payload,
      }));
      const r = await api.post("/back-office-sync/flush", { docs });
      const results = r?.data?.results || [];
      const acked = results
        .filter((x) => x.status === "created" || x.status === "duplicate")
        .map((x) => x.client_uuid);
      const errors = results.filter((x) => x.status === "error");
      if (acked.length) {
        await removeBackOfficeDocs(acked);
        toast.success(
          `Synced ${acked.length} back-office doc${acked.length > 1 ? "s" : ""}`,
        );
      }
      if (errors.length) {
        toast.error(
          `${errors.length} back-office doc${errors.length > 1 ? "s" : ""} failed to sync — will retry`,
        );
      }
      await refreshCount();
      return r.data;
    } catch (e) {
      return { error: e.message };
    } finally {
      flushingRef.current = false;
      setFlushing(false);
    }
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    const onUpd = () => refreshCount();
    window.addEventListener("bo-queue-updated", onUpd);
    return () => window.removeEventListener("bo-queue-updated", onUpd);
  }, [refreshCount]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      flushNow();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    const iv = setInterval(() => {
      if (navigator.onLine) flushNow();
    }, 30_000);

    if (navigator.onLine) flushNow();

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(iv);
    };
  }, [flushNow]);

  return { online, pending, flushing, flushNow, refreshCount };
}
